import Loader from 'components/ui/LoadingIndicator';
import {
    Box,
    Button,
    GlobalStyles,
    Link as MuiLink,
    Typography,
} from '@mui/material';
import { MessageView } from 'components/ui/Feedback';
import { themeVars } from '@mds/ui';
import React, { FC, ReactNode } from 'react';

import { getPublishedData } from '../../ui/PublishedBy';
import ArticleContent from '../../ui/ArticleContent';
import gql from 'graphql-tag';
import { getLinkPrefix } from '../../../utils/list-utils';
import { useActivityLogger } from '../../hooks/use-activity-logger';
import { useViewStats } from '../../hooks/use-view-stats';
import { formatDisplayDateTime } from '../../../utils/date.utils';
import { promotionalContentShortcodesToHtml } from '../../../utils/promotional-content.utils';
import Disclaimer from '../../ui/Disclaimer';
import { ArticleState } from '../../../shared/global-enums';
import { translateErrorMessage } from '../../../shared/error-handling';
import { plainText } from '../../../utils/string.utils';
import { sanitizeRouteId } from '../../../utils/route-params.utils';
import { isValidMongoId } from '../../../utils/id-validation.utils';

import { useQuery } from '@apollo/react-hooks';
import { Article } from '../../../shared/types';
import { useParams, useHistory } from 'react-router-dom';

interface MarketingArticleWrapperProps {
    centeredWrapper: boolean;
    children: ReactNode;
}

const FIND_ARTICLE_BY_ID = gql`
    query findMarketingArticleById($id: ID!) {
        article: findMarketingArticleById(id: $id) {
            _id
            title
            html
            publishedBy {
                _id
                name
                type
            }
            tags {
                _id
                name
            }
            categories {
                _id
                name
            }
            summary
            publishDate
            lastEdited
            version
            state
            canSeeFullVersion
        }
    }
`;

const MarketingArticlePage: React.FC = () => {
    const { _id: routeId } = useParams<{ _id?: string }>();
    const _id = sanitizeRouteId(routeId);
    const isValidArticleId = isValidMongoId(_id);
    const { push } = useHistory();
    const addViewStats = useViewStats();
    const archivedOrPending = (state: ArticleState) =>
        state === ArticleState.Archived || state === ArticleState.Pending;
    const MarketingArticleWrapper: FC<MarketingArticleWrapperProps> = ({
        children,
        centeredWrapper,
    }) => (
        <Box
            sx={{
                bgcolor: 'background.paper',
                maxWidth: 700,
                m: centeredWrapper ? { xs: 4, md: '64px auto' } : '50px',
                ...(!centeredWrapper
                    ? { '&>*': { mb: 4 } }
                    : {
                          '& > div:first-of-type': {
                              flexDirection: { xs: 'column', md: 'row' },
                          },
                      }),
            }}
        >
            <GlobalStyles
                styles={(theme) => ({
                    body: {
                        backgroundColor:
                            themeVars(theme).palette.background.default,
                        color: themeVars(theme).palette.text.primary,
                    },
                    '#app-header': {
                        display: 'none',
                    },
                    '@media print': {
                        body: {
                            fontFamily: '"Inter", sans-serif',
                        },
                    },
                })}
            />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <img
                    height="40"
                    alt="Marex"
                    src="/insights/images/marex-dark-logo.png"
                />
                <MuiLink
                    sx={{
                        color: 'text.secondary',
                        '&:visited': { color: 'text.secondary' },
                    }}
                    href="http://www.marex.com"
                >
                    www.marex.com
                </MuiLink>
            </Box>
            {children}
        </Box>
    );

    const { data, loading, error } = useQuery<{ article: Article }>(
        FIND_ARTICLE_BY_ID,
        {
            skip: !isValidArticleId,
            variables: { id: _id },
            fetchPolicy: 'network-only',
            onCompleted: (data) => {
                const article = data?.article;
                if (!article) return;

                if (!archivedOrPending(article.state)) {
                    addViewStats(article);
                }
            },
        }
    );
    const article = data?.article;
    useActivityLogger('MarketingArticle', article?.title, article?._id);

    if (!isValidArticleId) {
        return (
            <MarketingArticleWrapper centeredWrapper={true}>
                <Box sx={{ mt: 11 }}>
                    <MessageView
                        styleName="primary"
                        size="large"
                        title="Article not found"
                        primaryAction={{
                            label: 'Return to Neon Insights',
                            onClick: () => push('/'),
                        }}
                    />
                </Box>
            </MarketingArticleWrapper>
        );
    }

    const copyTextToClipboard = () => {
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(
                plainText(data?.article.title + ' \n' + data?.article.html)
            );
        }
    };

    if (error) {
        return (
            <MarketingArticleWrapper centeredWrapper={!!error}>
                <Box sx={{ mt: 11, '& h1': { color: 'text.secondary' } }}>
                    <MessageView
                        styleName="primary"
                        size="large"
                        title={translateErrorMessage(error?.message || '')}
                        primaryAction={{
                            label: 'Return to Neon Insights',
                            onClick: () => push('/'),
                        }}
                    ></MessageView>
                </Box>
            </MarketingArticleWrapper>
        );
    }

    if (loading || !article) return <Loader />;

    const publishedData = getPublishedData(
        article,
        getLinkPrefix(),
        false,
        article.version > 1
    );

    if (!article.canSeeFullVersion) {
        return <></>;
    }

    return (
        <MarketingArticleWrapper
            centeredWrapper={archivedOrPending(article.state)}
        >
            {archivedOrPending(article.state) ? (
                <Box sx={{ mt: 11, '& h1': { color: 'text.secondary' } }}>
                    <MessageView
                        styleName="primary"
                        size="large"
                        title="This article is no longer available."
                        primaryAction={{
                            label: 'Return to Neon Insights',
                            onClick: () => push('/'),
                        }}
                    >
                        <Typography
                            variant="body1"

                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            This article was archived or unpublished by a
                            contributor.
                        </Typography>
                    </MessageView>
                </Box>
            ) : (
                <>
                    <Box>
                        <Box
                            sx={{ bgcolor: 'primary.main', mb: 2, p: 2 }}
                            id={'article-page-title'}
                        >
                            <Typography variant="h5">
                                {article.title}
                            </Typography>
                        </Box>
                        <Box id={'article-page-published-by'}>
                            <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                    color: 'text.secondary',
                                }}
                            >
                                Published{' '}
                                {formatDisplayDateTime(
                                    publishedData.date,
                                    publishedData.momentInTimeFormat
                                )}{' '}
                                in
                            </Typography>
                            <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                    color: 'text.primary',
                                }}
                            >
                                <strong>
                                    {' '}
                                    {article.categories
                                        .map((c) => c.name)
                                        .join(', ')}{' '}
                                </strong>
                            </Typography>
                            <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                    color: 'text.secondary',
                                }}
                            >
                                by
                            </Typography>
                            <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                    color: 'text.primary',
                                }}
                            >
                                <strong> {article.publishedBy.name} </strong>
                            </Typography>
                            <Box
                                component="span"
                                title="Copy text to clipboard"
                            >
                                <Button
                                    sx={{ float: 'right' }}
                                    variant="contained"
                                    onClick={copyTextToClipboard}
                                >
                                    Export
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        <Box component="span" id={'article-page-summary'}>
                            {article.summary}
                        </Box>
                    </Typography>
                    <ArticleContent
                        title={article.title}
                        html={
                            article?.html
                                ? promotionalContentShortcodesToHtml(
                                      article.html,
                                      []
                                  )
                                : ''
                        }
                        id={article._id}
                        isMarketing={true}
                    />
                    <Disclaimer />
                </>
            )}
        </MarketingArticleWrapper>
    );
};

export default MarketingArticlePage;
